import React, { FC } from "react"
import styled from "styled-components"
import { BrandColor, BrandIcon, theme } from "../../../util/styles/theme"
import { BoxSize } from "../../box/Box"
import { CollapsibleContext } from "../../collapsible/components/CollapsibleContext"
import { TagGroup, VariantTagItem } from "../../tag-group/TagGroup"
import { Icon } from "../../icon/Icon"

export interface CardHeaderProps {
  className?: string
  backgroundColor?: BrandColor
  icon?: BrandIcon
  iconColor?: BrandColor
  size?: BoxSize
  tags?: VariantTagItem[]
}

type ContainerProps = {
  isCollapsible?: boolean
  withIcon: boolean
  withTags: boolean
} & Pick<CardHeaderProps, "backgroundColor" | "size">

const Container = styled.div<ContainerProps>`
  cursor: ${({ isCollapsible }) => (isCollapsible ? "pointer" : undefined)};
  background: ${({ backgroundColor }) =>
    backgroundColor ? theme.colors[backgroundColor] : undefined};

  display: flex;
  flex-direction: ${({ withIcon, withTags }) =>
    withIcon && withTags ? "row-reverse" : "column"};
  gap: ${({ withIcon }) => (withIcon ? theme.spacings.small : undefined)};
  justify-content: ${({ withIcon, withTags }) =>
    withIcon && withTags ? "space-between" : undefined};

  position: relative;
  padding: ${({ size }) => size && theme.borderRadius[size]};

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
  ${theme.textStyle("heading", "large", "medium")};

  ${theme.breakpoints.sm} {
    ${theme.textStyle("heading", "xlarge", "medium")};
  }
`

const StyledTagGroup = styled(TagGroup)``
const TagAndHeadingContainer = styled.div``

export const CardHeader: FC<CardHeaderProps> = ({
  className,
  backgroundColor,
  icon,
  iconColor,
  size,
  tags,
  children,
}) => {
  const context = React.useContext(CollapsibleContext)

  return (
    <Container
      className={className}
      onClick={context.onToggleCollapsed}
      isCollapsible={context.isCollapsible}
      size={size}
      backgroundColor={backgroundColor}
      withIcon={Boolean(icon)}
      withTags={Boolean(tags)}
    >
      {icon && <StyledIcon icon={icon} color={iconColor || "orange"} />}
      {tags && (
        <TagAndHeadingContainer>
          {tags && <StyledTagGroup tags={tags} size="small" />}
          {children}
        </TagAndHeadingContainer>
      )}
      {!tags && children}
    </Container>
  )
}

CardHeader.defaultProps = {
  size: "medium",
}
